/**
 * The agent side panel's TOC tab (#1273 D1).
 *
 * The job's table of contents, drawn with the same <Tree> the Gen AI table
 * picker uses (TableSourcePicker) so the two surfaces cannot drift — search
 * included. Clicking a table chips the composer; it never sends. The tick is a
 * mirror of the chips, so unticking removes the chip.
 *
 * Job picker (8 Sep, Matt): the same job-first picker TableSourcePicker uses,
 * so a session can reference tables from a job other than its own. Unlike the
 * dashboard editor, nothing here provides a DashboardJobsProvider from a
 * dashboard's own job set — this builds one itself from the account context's
 * display names, restricted to `sessionJobs` (T18): a job the session hasn't
 * selected cannot be reached from this dropdown, full stop. `treeJob` recomputes
 * from `sessionJobs` on every render, so removing the currently-shown job from
 * the selection moves the dropdown to another selected one with no effect
 * needed — the prop change alone drives it.
 *
 * Platinum-specific by design: agentkit knows only that the host supplied a tab.
 */
import React, { useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import Tree from '../tree/Tree'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeTitle } from '../../utils/nodeTypes'
import { getInitialExpandedIds } from '../../utils/tree'
import { getAugementedTOC } from '../../utils/multitables'
import { API_BASE_URL } from '../../constants/system'
import { buildTableChip, type ComposerChip } from '../../hooks/useComposerChips'
import useAccount from '../../hooks/useAccount'
import TreePreviewAction from './TreePreviewAction'
import { DashboardJobsProvider, DashboardJobSelect, type DashboardJobOption } from '../dashboard/DashboardJobs'

export interface AgentTocTabProps {
  customer: string
  /** The session's job. Empty until the session has resolved. */
  job: string
  /**
   * The session's selected job set (T18). The job dropdown offers only these —
   * a job outside this list cannot be reached from it, and the default
   * selection is its first entry.
   */
  sessionJobs: string[]
  /** Ids of the tables currently chipped, so the tree can mirror them. */
  selectedIds: Set<string>
  /** Add (on=true) or remove (on=false) a table's chip. */
  onToggle: (chip: ComposerChip, on: boolean) => void
  /** Show the table without attaching it. Clicking its NAME does this. */
  onPreview?: (chip: ComposerChip) => void
}

export default function AgentTocTab({ customer, job, sessionJobs, selectedIds, onToggle, onPreview }: AgentTocTabProps) {
  const [expanded, setExpanded] = useState<string[]>([])
  const [treeSelected, setTreeSelected] = useState<string>('')
  // The same job-first pattern as TableSourcePicker (#1229 C4): pick a job,
  // but only a SELECTED one sticks — if it falls out of sessionJobs (removed
  // from the session, or never in it) this falls straight through to the
  // first selected job. No effect: this recomputes on every render, so a
  // sessionJobs change alone moves the tree.
  const [pickJob, setPickJob] = useState('')
  const treeJob = (pickJob && sessionJobs.includes(pickJob)) ? pickJob : (sessionJobs[0] || job)

  const account = useAccount()
  const jobOptions: DashboardJobOption[] = useMemo(() => {
    const sessionCust = account.user?.session_info?.sessionCusts?.find(c => c.name === customer)
    return (sessionCust?.sessionJobs || [])
      .filter((j): j is { name: string; displayName?: string | null } => Boolean(j.name))
      .filter(j => sessionJobs.includes(j.name))
      .map(j => ({ name: j.name, displayName: j.displayName || undefined }))
  }, [account.user, customer, sessionJobs])

  const tocQuery = useQuery({
    queryKey: ['toc', customer, treeJob],
    enabled: Boolean(customer && treeJob),
    queryFn: async () => {
      const res = await axios.get<{ toc: GenNode[] }>(`${API_BASE_URL}/job/${customer}/${treeJob}/toc`)
      return res.data.toc || []
    },
  })

  const rawToc = tocQuery.data || []
  const augmentedToc = useMemo(() => getAugementedTOC({ toc: rawToc, multiTables: [] }), [rawToc])
  const toc = augmentedToc.nodes

  // Open the folders the TOC flags expanded, once per loaded TOC — render-phase
  // ref guard, nothing to clean up (same shape as TableSourcePicker, #1229 B).
  const expandedInitRef = useRef<GenNode[] | null>(null)
  if (toc.length > 0 && expandedInitRef.current !== toc) {
    expandedInitRef.current = toc
    setExpanded(getInitialExpandedIds(toc))
  }

  const jobLabel = jobOptions.find(j => j.name === treeJob)?.displayName
  const chipFor = (node: GenNode): ComposerChip => buildTableChip({
    jobLabel,
    displayName: getNodeTitle(node),
    folderPath: node.value2 || '',
    filename: node.value1 || '',
    job: treeJob,
  })

  const handleNodeSelectToggle = (itemId: string, node: GenNode, isNodeSelected: boolean) => {
    if (!isNodeSelected) return
    setTreeSelected(itemId)
    if (node.type !== 'Table') return
    // The NAME shows the table; the TICK attaches it. Clicking a name used to
    // attach, which meant you could not look at a table without committing to
    // sending it — and the only way to undo a misclick was to find the chip.
    onPreview?.(chipFor(node))
  }

  const handleNodeCheckedToggle = (_itemId: string, node: GenNode, isChecked: boolean) => {
    if (node.type !== 'Table') return
    onToggle(chipFor(node), isChecked)
  }

  const handleNodeExpandToggle = (itemId: string, _node: GenNode, isExpanded: boolean) => {
    setExpanded(isExpanded ? [...expanded, itemId] : expanded.filter(id => id !== itemId))
  }

  return (
    <DashboardJobsProvider value={{ jobs: jobOptions, primaryJob: job }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, overflow: 'hidden', gap: 1 }}>
        <DashboardJobSelect
          value={treeJob}
          onChange={(j) => { setPickJob(j); setTreeSelected('') }}
        />
        {!treeJob ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1 }}>
            This session has no job, so there is no table of contents to show.
          </Typography>
        ) : tocQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Tree
            searchEnabled
            searchPlaceholder="Search tables..."
            nodes={toc}
            expanded={expanded}
            selected={treeSelected}
            getCheckboxState={node => node.type === 'Table'
              ? { enabled: true, checked: selectedIds.has(chipFor(node).id) }
              : { enabled: false, checked: false }}
            getNodeTooltip={node => node.type === 'Table' ? chipFor(node).tooltip : undefined}
        getNodeActions={node => node.type === 'Table' && onPreview
          ? <TreePreviewAction label={chipFor(node).label} onPreview={() => onPreview(chipFor(node))} />
          : null}
            onNodeSelectToggle={handleNodeSelectToggle}
            onNodeCheckedToggle={handleNodeCheckedToggle}
            onNodeExpandToggle={handleNodeExpandToggle}
          />
        )}
      </Box>
    </DashboardJobsProvider>
  )
}
